import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import axios from 'axios';

// Importa tus componentes de Soft UI (ajusta la ruta según tu estructura)
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftButton from 'components/SoftButton';

// Importa SweetAlert2
import Swal from 'sweetalert2';

/**
 * Componente para editar la información de una fila (Cost PP, Cost CC, Income PP, Income CC).
 * @param {Object} props
 * @param {boolean} props.show - Controla la visualización del modal.
 * @param {Function} props.onClose - Función para cerrar el modal.
 * @param {Object} props.rowData - Datos de la fila: { id_magaya, costpp, costcc, incpp, inccc }.
 * @param {Function} props.onUpdated - Función que se llama cuando la actualización es exitosa (para refrescar la tabla, etc.).
 */
function EditRowModal({ show, onClose, rowData, onUpdated }) {
  // rowData debería contener: { id_magaya, costpp, costcc, incpp, inccc }
  const [costPP, setCostPP] = useState('');
  const [costCC, setCostCC] = useState('');
  const [incomePP, setIncomePP] = useState('');
  const [incomeCC, setIncomeCC] = useState('');

  // Sincroniza los valores cuando cambia rowData o se abre el modal
  useEffect(() => {
    if (rowData) {
      setCostPP(rowData.costpp || 0);
      setCostCC(rowData.costcc || 0);
      setIncomePP(rowData.incpp || 0);
      setIncomeCC(rowData.inccc || 0);
    }
  }, [rowData]);

  // Función auxiliar para mostrar un toast con SweetAlert2
  const showToast = (icon, title) => {
    Swal.fire({
      icon,
      title,
      toast: true,
      position: 'top-end',
      timer: 3000,
      showConfirmButton: false,
    });
  };

  // Maneja la acción de guardar
  const handleSave = () => {
    // Validaciones simples
    if (isNaN(costPP) || isNaN(costCC) || isNaN(incomePP) || isNaN(incomeCC)) {
      showToast('error', 'Todos los campos deben ser numéricos');
      return;
    }

    // Petición al endpoint Django (ejemplo: /api/finance/update-magaya-item/)
    axios
      .patch(`${process.env.REACT_APP_SERVER_URL}/api/finance/consolidated/charges/`, {
        id: rowData.id_magaya, // id del registro en la tabla magaya
        costpp: costPP,
        costcc: costCC,
        incomepp: incomePP,
        incomecc: incomeCC,
      })
      .then((response) => {
        // Mostramos el mensaje de éxito con un toast
        showToast('success', response.data.message);
        // Llamamos a onUpdated() para refrescar la tabla en el componente padre
        onUpdated();
        onClose();
      })
      .catch((error) => {
        console.error('Error al actualizar la información:', error);
        showToast('error', 'No se pudo actualizar la información, intente nuevamente.');
      });
  };

  return (
    <Modal show={show} onHide={onClose} size="md" centered backdrop="static">
      <Modal.Header closeButton>
        <SoftTypography variant="h5" fontWeight="bold">
          Actualizar información
        </SoftTypography>
      </Modal.Header>
      <Modal.Body>
        <SoftBox display="flex" flexDirection="column" gap={2}>
          <SoftTypography variant="caption">Cost PP</SoftTypography>
          <input
            type="number"
            value={costPP}
            onChange={(e) => setCostPP(e.target.value)}
            placeholder="0.00"
            className="form-control"
          />

          <SoftTypography variant="caption">Cost CC</SoftTypography>
          <input
            type="number"
            value={costCC}
            onChange={(e) => setCostCC(e.target.value)}
            placeholder="0.00"
            className="form-control"
          />

          <SoftTypography variant="caption">Income PP</SoftTypography>
          <input
            type="number"
            value={incomePP}
            onChange={(e) => setIncomePP(e.target.value)}
            placeholder="0.00"
            className="form-control"
          />

          <SoftTypography variant="caption">Income CC</SoftTypography>
          <input
            type="number"
            value={incomeCC}
            onChange={(e) => setIncomeCC(e.target.value)}
            placeholder="0.00"
            className="form-control"
          />
        </SoftBox>
      </Modal.Body>
      <Modal.Footer>
        <SoftButton color="success" variant="gradient" onClick={handleSave}>
          Guardar
        </SoftButton>
      </Modal.Footer>
    </Modal>
  );
}

EditRowModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  rowData: PropTypes.shape({
    id_magaya: PropTypes.number,
    costpp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    costcc: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    incpp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    inccc: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onUpdated: PropTypes.func.isRequired,
};

EditRowModal.defaultProps = {
  rowData: null,
};

export default EditRowModal;
